import { Cast, Edit, Image, PlaylistAdd, VideoFile } from '@mui/icons-material'
import { Box, Grid2 as Grid, IconButton, Skeleton, Typography, useMediaQuery, useTheme } from '@mui/material'
import { ReactNode, useEffect, useState } from 'react'
import { SectionCard, StatusChip } from '../Components/ui'
import { ds } from '../theme/ds'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { InfoDTO } from '../Model/InfoDTO'
import { useFindAllMediaQuery } from '../service/MediaService'
import { useFindAllPlaylistsQuery } from '../service/PlaylistService'
import { useLazyGetSystemInfoQuery } from '../service/SysInfoService'
import { useAppSelector } from '../store/hooks'

// ─── Mapa SVG ────────────────────────────────────────────────────────────────
const MapSVG = ({ isDark }: { isDark: boolean }) => {
  const stroke = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)'
  const road   = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)'
  const block  = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.03)'
  return (
    <svg viewBox="0 0 200 160" style={{ width: '100%', height: '100%' }}>
      <defs>
        <pattern id="pg" width="20" height="20" patternUnits="userSpaceOnUse">
          <path d="M 20 0 L 0 0 0 20" fill="none" stroke={stroke} strokeWidth="0.5" />
        </pattern>
      </defs>
      <rect width="200" height="160" fill="url(#pg)" />
      <line x1="0" y1="60"  x2="200" y2="60"  stroke={road} strokeWidth="1.5" />
      <line x1="0" y1="100" x2="200" y2="100" stroke={road} strokeWidth="1" />
      <line x1="60"  y1="0" x2="60"  y2="160" stroke={road} strokeWidth="1.5" />
      <line x1="120" y1="0" x2="120" y2="160" stroke={road} strokeWidth="1" />
      {[[10,10,35,35],[70,10,35,35],[130,10,25,35],[10,70,35,20],[70,70,35,20],[10,110,25,30],[70,110,35,30],[130,110,50,30]].map(([x,y,w,h],i)=>(
        <rect key={i} x={x} y={y} width={w} height={h} rx="3" fill={i===0||i===7?'rgba(20,184,160,0.04)':block} stroke={i===0||i===7?'rgba(20,184,160,0.1)':road} strokeWidth="0.5"/>
      ))}
      {[[45,28],[90,52],[140,80],[55,120],[170,35]].map(([cx,cy],i)=>(
        <g key={i}>
          <circle cx={cx} cy={cy} r="7" fill="#14b8a0" opacity="0.15"/>
          <circle cx={cx} cy={cy} r="4" fill="#14b8a0" opacity="0.9"/>
        </g>
      ))}
    </svg>
  )
}


// ─── StatCard ─────────────────────────────────────────────────────────────────
type BreakdownItem = { icon: ReactNode; label: string; value: number }
type StatCardProps = {
  variant?: 'primary' | 'default'
  icon: ReactNode
  label: string
  value?: string | number
  sub?: string
  breakdown?: BreakdownItem[]
  total?: number
  loading?: boolean
  onClick?: () => void
}

const StatCard = ({ variant = 'default', icon, label, value, sub, breakdown, total, loading, onClick }: StatCardProps) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isPrimary = variant === 'primary'

  const bg   = isDark ? '#152028' : '#ffffff'
  const bdr  = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
  const teal = isDark ? '#14b8a0' : '#0d9488'

  const iconBox = (
    <Box sx={{
      width: isMobile ? ds.height.kpiIconSm : ds.height.kpiIcon,
      height: isMobile ? ds.height.kpiIconSm : ds.height.kpiIcon,
      flexShrink: 0,
      borderRadius: '50%',
      background: isPrimary
        ? `linear-gradient(135deg, ${teal} 0%, ${isDark ? '#0d9488' : '#0a7a70'} 100%)`
        : (isDark ? 'linear-gradient(135deg, #1a3540, #142830)' : 'linear-gradient(135deg, #e8f5f3, #d0ede9)'),
      boxShadow: isPrimary ? '0 6px 20px rgba(20,184,160,0.4)' : 'none',
      border: isPrimary ? 'none' : `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(13,148,136,0.15)'}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: isPrimary ? '#fff' : teal,
    }}>
      {icon}
    </Box>
  )

  // ── Mobile: layout horizontal ──────────────────────────────────────────────
  if (isMobile) {
    return (
      <Box onClick={onClick} sx={{
        bgcolor: isPrimary ? undefined : bg,
        background: isPrimary
          ? (isDark ? 'linear-gradient(145deg, #0f2e35 0%, #0c2129 100%)' : 'linear-gradient(145deg, #e6f7f5 0%, #d0f0ec 100%)')
          : undefined,
        border: `1px solid ${isPrimary ? (isDark ? 'rgba(20,184,160,0.45)' : 'rgba(13,148,136,0.35)') : bdr}`,
        boxShadow: isPrimary ? (isDark ? '0 0 0 1px rgba(20,184,160,0.2)' : '0 2px 12px rgba(13,148,136,0.12)') : 'none',
        borderRadius: '14px', p: '14px 16px',
        display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1.5,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'opacity 0.15s',
        '&:active': onClick ? { opacity: 0.75 } : {},
        minHeight: 64,
      }}>
        {iconBox}

        {/* Texto + valor */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary', lineHeight: 1.2, mb: 0.25 }}>
            {label}
          </Typography>
          {value !== undefined && (
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: 'text.primary', lineHeight: 1 }}>
              {loading ? <Skeleton width={32} height={26} /> : value}
            </Typography>
          )}
          {sub && !breakdown && (
            <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{sub}</Typography>
          )}
          {breakdown && (
            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
              {breakdown.map((item, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ fontSize: 12, display: 'flex', color: isDark ? '#7fa3b0' : '#4a7080' }}>{item.icon}</Box>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{item.label}</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary' }}>
                    {loading ? <Skeleton width={16} height={14} /> : item.value}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Valor à direita + seta se clicável */}
        {onClick && value !== undefined && (
          <Typography sx={{ fontSize: 13, color: 'text.disabled', flexShrink: 0 }}>→</Typography>
        )}
        {total !== undefined && (
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={{ fontSize: 10, color: 'text.disabled', lineHeight: 1 }}>Total</Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: teal, lineHeight: 1.2 }}>
              {loading ? <Skeleton width={24} height={20} /> : total}
            </Typography>
          </Box>
        )}
      </Box>
    )
  }

  // ── Desktop: horizontal compacto ──────────────────────────────────────────
  const desktopIcon = (
    <Box sx={{
      width: 40, height: 40, flexShrink: 0,
      borderRadius: `${ds.radius.md}px`,
      background: isPrimary
        ? `linear-gradient(135deg, ${teal} 0%, ${isDark ? '#0d9488' : '#0a7a70'} 100%)`
        : (isDark ? 'rgba(20,184,160,0.12)' : 'rgba(13,148,136,0.08)'),
      boxShadow: isPrimary ? '0 4px 12px rgba(20,184,160,0.35)' : 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: isPrimary ? '#fff' : teal,
    }}>
      {icon}
    </Box>
  )

  return (
    <Box onClick={onClick} sx={{
      bgcolor: isPrimary ? undefined : bg,
      background: isPrimary
        ? (isDark ? ds.gradient.tealKpi : ds.gradient.tealKpiL)
        : undefined,
      border: `1px solid ${isPrimary ? (isDark ? 'rgba(20,184,160,0.4)' : 'rgba(13,148,136,0.3)') : bdr}`,
      boxShadow: isPrimary
        ? (isDark ? '0 0 0 1px rgba(20,184,160,0.15)' : '0 2px 12px rgba(13,148,136,0.1)')
        : (isDark ? 'none' : ds.shadow.card),
      borderRadius: `${ds.radius.card}px`,
      p: '12px 16px',
      display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2,
      cursor: onClick ? 'pointer' : 'default',
      transition: `all ${ds.motion.normal}`,
      minHeight: 72,
      '&:hover': onClick ? {
        transform: 'translateY(-1px)',
        boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.25)' : '0 6px 20px rgba(0,0,0,0.1)',
        borderColor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.14)',
      } : {},
    }}>
      {desktopIcon}

      {/* Label + valor principal */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 11, fontWeight: ds.font.medium, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1, mb: 0.75 }}>
          {label}
        </Typography>
        {value !== undefined && (
          <Typography sx={{ fontSize: 24, fontWeight: ds.font.bold, color: 'text.primary', lineHeight: 1 }}>
            {loading ? <Skeleton width={40} height={28} /> : value}
          </Typography>
        )}
        {sub && !breakdown && (
          <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.4 }}>{sub}</Typography>
        )}
        {breakdown && (
          <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
            {breakdown.map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ fontSize: 12, display: 'flex', color: isDark ? '#7fa3b0' : '#4a7080' }}>{item.icon}</Box>
                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{item.label}</Typography>
                <Typography sx={{ fontSize: 12, fontWeight: ds.font.semibold, color: 'text.primary' }}>
                  {loading ? <Skeleton width={16} height={14} /> : item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Direita: total ou seta */}
      {total !== undefined && (
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 10, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Total</Typography>
          <Typography sx={{ fontSize: 20, fontWeight: ds.font.bold, color: teal, lineHeight: 1.2 }}>
            {loading ? <Skeleton width={24} height={22} /> : total}
          </Typography>
        </Box>
      )}
      {onClick && !breakdown && !total && (
        <Typography sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0, opacity: 0.5 }}>›</Typography>
      )}
    </Box>
  )
}

// ─── DashboardPage ────────────────────────────────────────────────────────────
const mock: InfoDTO = {
  app: { userCount: 0, activeUserCunt: 0, priceTableCount: 0, playlistCount: 0, midiaCount: 0, imageCount: 0, videoCount: 0, midiaSize: 0, deviceCount: 0, onlineDeviceCount: 0 },
  sys: { cpuUsage: 0, uptime: 0, memoryAvailable: 0, memoryUsed: 0 },
}

const DashboardPage = () => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const user = useAppSelector(s => s.auth.user)
  const allPlaylists = useAppSelector(s => s.playlist)
  const playlists = [...allPlaylists].sort((a, b) => (b.items?.length ?? 0) - (a.items?.length ?? 0))
  const [info, setInfo] = useState(mock)
  const [loading, setLoading] = useState(true)
  const [trigger] = useLazyGetSystemInfoQuery()
  useFindAllPlaylistsQuery(user?.id?.toString() || '', { skip: !user?.id || allPlaylists.length > 0 })
  const { data: medias = [] } = useFindAllMediaQuery(user?.id?.toString() || '', { skip: !user?.id })

  useEffect(() => {
    trigger().then(r => { if (r.data) setInfo(r.data) })
      .catch(() => toast.error('Falha ao carregar informações'))
      .finally(() => setLoading(false))
  }, [])

  const images   = medias.filter(m => m.type?.startsWith('image')).length
  const videos   = medias.filter(m => m.type?.startsWith('video')).length

  const bg         = isDark ? '#152028' : '#ffffff'
  const bdr        = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
  const rowDivider = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)'
  const teal       = isDark ? '#14b8a0' : '#0d9488'
  const px         = { xs: 2, sm: 3 }

  const displays = [
    { name: 'Display 1', loc: 'Entrada', online: true  },
    { name: 'Display 2', loc: 'Loja',    online: false },
    { name: 'Display 3', loc: 'Caixa',   online: true  },
    { name: 'Display 4', loc: 'Vitrine', online: false },
  ]
  const displaysOnline = displays.filter(d => d.online).length

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', overflowX: 'hidden', bgcolor: 'background.default' }}>

      {/* ── KPI cards ── */}
      <Grid container spacing={{ xs: 1.25, sm: 1.5 }} sx={{ px, pt: { xs: 2, sm: 2.5 } }}>
        <Grid size={{ xs: 12, sm: 3 }}>
          <StatCard variant="primary" icon={<PlaylistAdd sx={{ fontSize: 20 }} />} label="Playlists"
            value={loading ? undefined : playlists.length} sub="Total cadastradas" loading={loading}
            onClick={() => navigate('/playlists')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <StatCard icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
              <circle cx="12" cy="13" r="2"/><polyline points="8.5 17 10 15.5 12 17 14 15.5 15.5 17"/>
            </svg>
          } label="Mídia" value={loading ? undefined : medias.length} sub="Total na biblioteca" loading={loading}
            onClick={() => navigate('/media')} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <StatCard icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
            </svg>
          } label="Mídias Upadas"
            breakdown={[
              { icon: <Image sx={{ fontSize: 13 }} />, label: 'Imagens', value: images },
              { icon: <VideoFile sx={{ fontSize: 13 }} />, label: 'Vídeos', value: videos },
            ]}
            total={images + videos}
            loading={loading} />
        </Grid>
        <Grid size={{ xs: 12, sm: 3 }}>
          <StatCard icon={<Cast sx={{ fontSize: 20 }} />}
            label="Displays" value={loading ? undefined : displaysOnline}
            sub={`de ${displays.length} conectados`} loading={loading}
            onClick={() => navigate('/displays')} />
        </Grid>
      </Grid>

      {/* ── Seções ── */}
      <Grid container spacing={{ xs: 1.25, sm: 1.5 }} sx={{ px, pt: { xs: 1.5, sm: 1.75 }, pb: 3 }}>

        {/* Playlists Overview — mais espaço no desktop */}
        <Grid size={{ xs: 12, sm: 7 }}>
          <SectionCard title="Visão Geral das Playlists" linkLabel="Ver todas →" onLink={() => navigate('/playlists')}>

            {playlists.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1, color: 'text.disabled' }}>
                <PlaylistAdd sx={{ fontSize: 36, opacity: 0.4 }} />
                <Typography variant="caption">Nenhuma playlist criada</Typography>
              </Box>
            ) : isMobile ? (
              /* Mobile — intocado */
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {playlists.slice(0, 4).map((p, i) => (
                  <Box key={p.id ?? i} sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    p: '10px 12px', borderRadius: '10px',
                    bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)',
                    border: `1px solid ${rowDivider}`,
                    '&:active': { opacity: 0.75 },
                  }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', mb: 0.25 }} noWrap>{p.name}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                        {p.items?.length ?? 0} {p.items?.length === 1 ? 'item' : 'itens'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      <StatusChip status="active" />
                      <IconButton size="small" onClick={() => navigate(`/playlists/edit/${p.id}`)}
                        sx={{ p: '6px', color: 'text.disabled', '&:hover': { color: 'text.primary' } }}>
                        <Edit sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              /* Desktop: tabela refinada */
              <>
                <Box sx={{
                  display: 'grid', gridTemplateColumns: '1fr 90px 56px 60px 44px',
                  pb: 1, mb: 0.5,
                  borderBottom: `1px solid ${rowDivider}`,
                }}>
                  {['Nome', 'Código', 'Itens', 'Status', ''].map(h => (
                    <Typography key={h} sx={{
                      fontSize: 10, fontWeight: ds.font.semibold, color: 'text.disabled',
                      textTransform: 'uppercase', letterSpacing: '0.6px',
                      textAlign: h === 'Nome' ? 'left' : 'center',
                    }}>{h}</Typography>
                  ))}
                </Box>
                {playlists.slice(0, 6).map((p, i) => (
                  <Box key={p.id ?? i} sx={{
                    display: 'grid', gridTemplateColumns: '1fr 90px 56px 60px 44px',
                    alignItems: 'center', py: 1,
                    borderBottom: i < Math.min(playlists.length, 6) - 1 ? `1px solid ${rowDivider}` : 'none',
                    borderRadius: `${ds.radius.xs}px`, px: 0.5,
                    transition: `background ${ds.motion.fast}`,
                    '&:hover': {
                      bgcolor: isDark ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.025)',
                      '& .row-action': { opacity: 1 },
                    },
                  }}>
                    <Typography sx={{ fontSize: 13, fontWeight: ds.font.medium, color: 'text.primary' }} noWrap>
                      {p.name}
                    </Typography>
                    <Typography sx={{
                      fontSize: 11, color: 'text.disabled', textAlign: 'center',
                      fontFamily: 'monospace', letterSpacing: 1,
                    }}>
                      {p.code ?? '—'}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                      {p.items?.length ?? 0}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <StatusChip status="active" />
                    </Box>
                    <Box className="row-action" sx={{ display: 'flex', justifyContent: 'center', opacity: 0, transition: `opacity ${ds.motion.fast}` }}>
                      <IconButton size="small" onClick={() => navigate(`/playlists/edit/${p.id}`)}
                        sx={{ p: 0.5, color: 'text.disabled', '&:hover': { color: teal } }}>
                        <Edit sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </>
            )}
          </SectionCard>
        </Grid>

        {/* Displays Status */}
        <Grid size={{ xs: 12, sm: 5 }}>
          <SectionCard title="Status dos Displays" linkLabel="Ver todos →" onLink={() => navigate('/displays')}>

            {isMobile ? (
              /* Mobile — intocado */
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {displays.map((d, i) => (
                  <Box key={i} sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    p: '10px 12px', borderRadius: '10px',
                    bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.025)',
                    border: `1px solid ${rowDivider}`,
                  }}>
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary', mb: 0.25 }}>{d.name}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{d.loc}</Typography>
                    </Box>
                    <StatusChip status={d.online ? 'online' : 'offline'} />
                  </Box>
                ))}
              </Box>
            ) : (
              /* Desktop: mapa + lista refinada */
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Mapa */}
                <Box sx={{
                  bgcolor: isDark ? '#0a1a22' : '#f0f7f6',
                  borderRadius: `${ds.radius.sm}px`,
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.06)'}`,
                  overflow: 'hidden', height: 120,
                }}>
                  <MapSVG isDark={isDark} />
                </Box>

                {/* Resumo online/offline */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {[
                    { label: 'Online', count: displaysOnline, color: teal, bg: isDark ? 'rgba(20,184,160,0.08)' : 'rgba(13,148,136,0.06)' },
                    { label: 'Offline', count: displays.length - displaysOnline, color: '#f87171', bg: 'rgba(248,113,113,0.06)' },
                  ].map(s => (
                    <Box key={s.label} sx={{
                      flex: 1, py: 1, px: 1.5, borderRadius: `${ds.radius.sm}px`,
                      bgcolor: s.bg, border: `1px solid ${s.color}22`,
                    }}>
                      <Typography sx={{ fontSize: 18, fontWeight: ds.font.bold, color: s.color, lineHeight: 1 }}>{s.count}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>{s.label}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Lista */}
                <Box>
                  {displays.map((d, i) => (
                    <Box key={i} sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      py: 0.875,
                      borderBottom: i < displays.length - 1 ? `1px solid ${rowDivider}` : 'none',
                    }}>
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: ds.font.medium, color: 'text.primary', lineHeight: 1.3 }}>{d.name}</Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{d.loc}</Typography>
                      </Box>
                      <StatusChip status={d.online ? 'online' : 'offline'} />
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </Box>
  )
}

export default DashboardPage
